/** Backend, eğitim akışı popup'ları için semantik `actionUrl` gönderir.
 *  Frontend bu URL'leri gerçek router rotalarına çevirir.
 *
 *  Bilinen semantik pattern'ler:
 *   - `/educations/:id/survey`      → `/dashboard/client-polls` + state.educationId (modal otomatik açılır)
 *   - `/educations/:id/exam`        → `/dashboard/client-exams/take/:id`
 *   - `/educations/:id/certificate` → `/dashboard/client-certificates`
 *
 *  Tanınmayan path'lerde fallback (beyaz ekran engelleme amaçlı):
 *   - Absolute URL (http/https) → null (yok sayılır)
 *   - `/dashboard/...`          → olduğu gibi
 *   - `/` ile başlayan diğerleri → başına `/dashboard` eklenir
 *   - Diğerleri                  → `/dashboard/<raw>`
 */

/** Backend'in sınav/anket/sertifika popup'ları için döndürdüğü aksiyon objesi. */
export interface EducationFollowUpAction {
  displayType: "popup";
  type: "exam" | "survey" | "certificate";
  title: string;
  message: string;
  buttonLabel: string;
  /** Semantik path — `resolveFollowUpAction()` ile router rotasına çevrilir. */
  actionUrl: string;
}

export interface ResolvedFollowUp {
  /** react-router `navigate(path, ...)` için hedef path */
  path: string;
  /** navigate'in state argümanı — modal auto-open için `educationId` vs. */
  state?: Record<string, unknown>;
}

const SURVEY_RE = /^\/?educations\/([^/]+)\/survey\/?$/i;
const EXAM_RE = /^\/?educations\/([^/]+)\/exam\/?$/i;
const CERT_RE = /^\/?educations\/([^/]+)\/certificate\/?$/i;

export function resolveFollowUpAction(
  raw: string | null | undefined,
): ResolvedFollowUp | null {
  const url = raw?.trim();
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return null;

  const surveyMatch = SURVEY_RE.exec(url);
  if (surveyMatch) {
    return {
      path: "/dashboard/client-polls",
      state: { educationId: surveyMatch[1] },
    };
  }
  const examMatch = EXAM_RE.exec(url);
  if (examMatch) {
    return { path: `/dashboard/client-exams/take/${examMatch[1]}` };
  }
  const certMatch = CERT_RE.exec(url);
  if (certMatch) {
    return { path: "/dashboard/client-certificates" };
  }

  if (url.startsWith("/dashboard/") || url === "/dashboard") {
    return { path: url };
  }
  if (url.startsWith("/")) {
    return { path: `/dashboard${url}` };
  }
  return { path: `/dashboard/${url}` };
}
